import {
  createWalletClient,
  custom,
  getAddress,
  http,
  numberToHex,
  type Address,
  type EIP1193Provider,
  type WalletClient,
} from 'viem';

import { config } from '../config.ts';

export type WalletInfo = { id: string; name: string; icon?: string; provider: EIP1193Provider };
export type Connected = { address: Address; client: WalletClient; name: string; id: string; provider?: EIP1193Provider };

type AnnounceEvent = CustomEvent<{
  info: { uuid: string; name: string; icon: string; rdns: string };
  provider: EIP1193Provider;
}>;

/** Wallets announced through EIP-6963, plus the legacy injected provider as a fallback. */
export function discoverWallets(onChange: (w: WalletInfo[]) => void): () => void {
  const found = new Map<string, WalletInfo>();
  const emit = () => onChange([...found.values()]);
  const onAnnounce = (e: Event) => {
    const { info, provider } = (e as AnnounceEvent).detail;
    const id = info.rdns || info.uuid;
    found.set(id, { id, name: info.name, icon: info.icon, provider });
    emit();
  };
  window.addEventListener('eip6963:announceProvider', onAnnounce);
  window.dispatchEvent(new Event('eip6963:requestProvider'));
  const legacy = (window as unknown as { ethereum?: EIP1193Provider }).ethereum;
  const t = setTimeout(() => {
    if (found.size === 0 && legacy) {
      found.set('injected', { id: 'injected', name: 'Browser wallet', provider: legacy });
      emit();
    }
  }, 300);
  return () => {
    clearTimeout(t);
    window.removeEventListener('eip6963:announceProvider', onAnnounce);
  };
}

/** Puts the wallet on the app chain: EIP-712 domains carry the chain id and wallets refuse a mismatch. */
async function ensureChain(provider: EIP1193Provider): Promise<void> {
  const current = Number(await provider.request({ method: 'eth_chainId' }));
  if (current === config.chainId) return;
  const chainId = numberToHex(config.chainId);
  try {
    await provider.request({ method: 'wallet_switchEthereumChain', params: [{ chainId }] });
  } catch (err) {
    if ((err as { code?: number }).code !== 4902) throw err;
    await provider.request({
      method: 'wallet_addEthereumChain',
      params: [
        {
          chainId,
          chainName: config.chain.name,
          nativeCurrency: config.chain.nativeCurrency,
          rpcUrls: [config.rpcUrl],
          blockExplorerUrls: config.explorer ? [config.explorer] : undefined,
        },
      ],
    });
  }
}

export async function connectWallet(w: WalletInfo): Promise<Connected> {
  const [account] = await w.provider.request({ method: 'eth_requestAccounts' });
  if (!account) throw new Error('no account authorized');
  await ensureChain(w.provider);
  const address = getAddress(account);
  return {
    address,
    name: w.name,
    id: w.id,
    provider: w.provider,
    client: createWalletClient({ account: address, chain: config.chain, transport: custom(w.provider), pollingInterval: 1_000 }),
  };
}

/** Anvil only: its dev accounts are unlocked on the node, which signs and sends for them. */
export async function localAccounts(): Promise<Address[]> {
  const res = await fetch(config.rpcUrl, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ jsonrpc: '2.0', id: 1, method: 'eth_accounts', params: [] }),
  });
  return ((await res.json()).result as string[]).map((a) => getAddress(a));
}

export function connectLocal(address: Address): Connected {
  return {
    address,
    name: 'Anvil dev account',
    id: `local:${address}`,
    client: createWalletClient({ account: address, chain: config.chain, transport: http(config.rpcUrl), pollingInterval: 1_000 }),
  };
}

/** Raised while the app itself moves the wallet to another chain (a deposit from it): that change is not the user leaving */
let holding = 0;
export async function holdChain<T>(fn: () => Promise<T>): Promise<T> {
  holding++;
  try {
    return await fn();
  } finally {
    holding--;
  }
}

/** Follows account switches and chain changes made in the wallet. */
export function watchProvider(provider: EIP1193Provider, onAccounts: (a: Address | undefined) => void): () => void {
  const onAccountsChanged = (accounts: string[]) => onAccounts(accounts[0] ? getAddress(accounts[0]) : undefined);
  const onChainChanged = (id: string) => {
    if (holding) return;
    if (Number(id) !== config.chainId) onAccounts(undefined);
  };
  provider.on('accountsChanged', onAccountsChanged);
  provider.on('chainChanged', onChainChanged);
  return () => {
    provider.removeListener('accountsChanged', onAccountsChanged);
    provider.removeListener('chainChanged', onChainChanged);
  };
}

const LAST = 'papurr:wallet';

export function rememberWallet(c: Connected | undefined): void {
  try {
    if (c) localStorage.setItem(LAST, c.id);
    else localStorage.removeItem(LAST);
  } catch {
    // storage blocked: the user reconnects by hand
  }
}

/** Reconnects the last wallet without a prompt, if it still authorizes this site and sits on the app chain. */
export async function restoreWallet(): Promise<Connected | undefined> {
  let id: string | null = null;
  try {
    id = localStorage.getItem(LAST);
  } catch {
    return undefined;
  }
  if (!id) return undefined;
  if (id.startsWith('local:')) return config.isLocal ? connectLocal(getAddress(id.slice(6))) : undefined;
  const w = await new Promise<WalletInfo | undefined>((resolve) => {
    let stop = () => {};
    const t = setTimeout(() => {
      stop();
      resolve(undefined);
    }, 800);
    stop = discoverWallets((ws) => {
      const hit = ws.find((x) => x.id === id);
      if (hit) {
        clearTimeout(t);
        stop();
        resolve(hit);
      }
    });
  });
  if (!w) return undefined;
  const [account] = (await w.provider.request({ method: 'eth_accounts' })) as string[];
  if (!account || Number(await w.provider.request({ method: 'eth_chainId' })) !== config.chainId) return undefined;
  const address = getAddress(account);
  return {
    address,
    name: w.name,
    id: w.id,
    provider: w.provider,
    client: createWalletClient({ account: address, chain: config.chain, transport: custom(w.provider), pollingInterval: 1_000 }),
  };
}
